import { User } from '@supabase/supabase-js';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Spinner } from '../../components/Spinner';
import { UserTeam, useUserTeamQuery } from '../../hooks/useUserTeam';
import { supabase } from '../../services/supabase';

// Define the shape of your context
interface AuthContextType {
  user: User | null;
  userData: UserTeam | null;
  userDataLoading: boolean;
  userDataError: Error | null;
  signIn: () => Promise<void>;
  signOut: () => void;
  checkIfUserExists: (user: User) => Promise<void>;
  loading: boolean;
  loginError: string | null;
}

// Create the context with a default value
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

// Create a custom hook to use the auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// AuthProvider component
export function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const {
    data: userData = null,
    isLoading: userDataLoading,
    error: userDataError,
  } = useUserTeamQuery(user);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const signIn = async (): Promise<void> => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin, // Redirect back to your app after authentication
      },
    });
    if (error) console.error('Error signing in:', error);
  };

  const signOut = async (): Promise<void> => {
    await supabase.auth.signOut();
  };

  useEffect(() => {
    // Initialize Freshworks widget when user data changes
    if (user && window.FreshworksWidget) {
      window.FreshworksWidget('identify', 'ticketForm', {
        name: (user.user_metadata as { full_name?: string })?.full_name || '',
        email: user.email || '',
      });
      window.FreshworksWidget('disable', 'ticketForm', ['name', 'email']);
    }
  }, [user]);

  const upsertUser = async (userDetails: User): Promise<void> => {
    const { error } = await supabase
      .from('users')
      .update({
        first_name: (
          userDetails.user_metadata as { full_name: string }
        ).full_name.split(' ')[0],
        last_name: (
          userDetails.user_metadata as { full_name: string }
        ).full_name.split(' ')[1],
        avatar_url: (userDetails.user_metadata as { avatar_url: string })
          .avatar_url,
        updated_at: new Date(),
      })
      .eq('id', userDetails.id);

    if (error) {
      console.error('Error updating user:', error);
    }
  };

  const checkIfUserExists = async (currentUser: User): Promise<void> => {
    const { data: emailExists, error } = await supabase
      .from('users')
      .select('email,id')
      .eq('email', currentUser.email)
      .maybeSingle<{
        id: string;
        email: string;
      }>();
    if (error) throw new Error(error.message);
    if (emailExists) {
      setUser({ ...currentUser, id: emailExists?.id });
      setLoginError(null);
      // Don't await the upsert here to prevent blocking
      upsertUser(currentUser).catch(console.error);
    } else {
      setLoginError('User does not exist');
      await signOut();
    }
  };

  useEffect(() => {
    // Initial session check
    const fetchSession = async (): Promise<void> => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        // await upsertUser(session.user);
      }
      setLoading(false);
    };

    fetchSession().catch(console.error);

    // Set up auth listener separately
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        checkIfUserExists(session?.user).catch(console.error);
      } else {
        setUser(null);
      }
    });

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array

  const value = useMemo(
    () => ({
      user,
      userData,
      userDataLoading,
      userDataError,
      signIn,
      signOut,
      loading,
      checkIfUserExists,
      loginError,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, userData, userDataLoading, userDataError, loading, loginError],
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen w-screen bg-gray-100">
        <Spinner />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
